import React, { useEffect } from "react";
import {
  Container,
  Grid,
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Badge,
  useColorModeValue,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Divider,
  Button,
  Image,
  Heading,
  useToast,
} from "@chakra-ui/react";
import { FiSearch, FiStar, FiFilter, FiShoppingBag } from "react-icons/fi";
import { Link as RouterLink, useSearchParams, useNavigate } from "react-router-dom";
import type { Product } from "../types";
import { products } from "../data/products";
import { categories } from "../data/categories";
import { useCart } from "../context/CartContext";

const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("purple.500", "gray.700");
  const { addToCart } = useCart();
  const toast = useToast();

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart`,
      status: "success",
      duration: 2000,
      isClosable: true,
      position: "top",
      variant: "subtle",
    });
  };

  return (
    <Box
      as={RouterLink}
      to={`/product/${product.id}`}
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      overflow="hidden"
      transition="all 0.2s"
      _hover={{
        transform: "translateY(-4px)",
        shadow: "lg",
        borderColor: "purple.500",
      }}
      cursor="pointer"
    >
      <Box position="relative">
        <img
          src={product.image}
          alt={product.name}
          style={{
            width: "100%",
            height: "200px",
            objectFit: "cover",
          }}
        />
        <Badge
          position="absolute"
          top={2}
          right={2}
          colorScheme="purple"
          fontSize="md"
          px={3}
          py={1}
        >
          {product.discount}% OFF
        </Badge>
      </Box>
      <VStack p={4} spacing={3} align="stretch">
        <Text fontWeight="bold" fontSize="lg">
          {product.name}
        </Text>
        <HStack>
          <Icon as={FiStar} color="yellow.400" />
          <Text>{product.rating} / 5.0</Text>
          <Text color="gray.500">({product.sold} sold)</Text>
        </HStack>
        <HStack>
          <Text fontSize="lg" fontWeight="bold" color="purple.500">
            Rs. {product.price.toFixed(2)}
          </Text>
          {product.originalPrice && (
            <Text fontSize="sm" textDecoration="line-through" color="gray.500">
              Rs. {product.originalPrice.toFixed(2)}
            </Text>
          )}
        </HStack>
        <Text color="gray.600" noOfLines={2}>
          {product.description}
        </Text>
        <Button
          colorScheme="purple"
          leftIcon={<FiShoppingBag />}
          onClick={handleAddToCart}
          borderRadius="full"
          _hover={{
            transform: "translateY(-1px)",
            shadow: "md",
          }}
        >
          Add to Cart
        </Button>
      </VStack>
    </Box>
  );
};

const AllProducts: React.FC = () => {
  const [sortBy, setSortBy] = React.useState("featured");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(
    null
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("purple.500", "gray.700");

  // Get search query from URL parameters
  useEffect(() => {
    const queryFromUrl = searchParams.get("search");
    if (queryFromUrl) {
      setSearchQuery(queryFromUrl);
    }
  }, [searchParams]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    if (newQuery) {
      setSearchParams({ search: newQuery });
    } else {
      setSearchParams({});
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchParams({});
  };

  const filteredProducts = React.useMemo(() => {
    let filtered = [...products];

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(
        (product) => product.category === selectedCategory
      );
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(query) ||
          product.description.toLowerCase().includes(query) ||
          product.category.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    switch (sortBy) {
      case "price-low":
        filtered.sort((a, b) => a.price - b.price);
        break;
      case "price-high":
        filtered.sort((a, b) => b.price - a.price);
        break;
      case "rating":
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case "sold":
        filtered.sort((a, b) => b.sold - a.sold);
        break;
      default:
        // Default sorting (featured) - keep original order
        break;
    }

    return filtered;
  }, [products, sortBy, searchQuery, selectedCategory]);

  return (
    <Container maxW="1400px" py={8}>
      <Grid templateColumns={{ base: "1fr", lg: "220px 1fr" }} gap={6}>
        {/* Category Sidebar */}
        <Box
          bg={bgColor}
          p={5}
          borderRadius="lg"
          borderWidth="1px"
          borderColor={borderColor}
          height="fit-content"
          display={{ base: "none", lg: "block" }}
        >
          <VStack align="stretch" spacing={4}>
            <Heading size="md">Categories</Heading>
            <Divider />
            <Button
              variant={!selectedCategory ? "solid" : "ghost"}
              colorScheme={!selectedCategory ? "purple" : "gray"}
              justifyContent="flex-start"
              onClick={() => setSelectedCategory(null)}
            >
              All Products
            </Button>
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? "solid" : "ghost"}
                colorScheme={selectedCategory === category.id ? "purple" : "gray"}
                justifyContent="flex-start"
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </Button>
            ))}
          </VStack>
        </Box>

        {/* Main Content */}
        <VStack spacing={6} align="stretch">
          <HStack spacing={4}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </InputGroup>
            <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="featured">Featured</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="rating">Highest Rated</option>
              <option value="sold">Most Sold</option>
            </Select>
          </HStack>

          {filteredProducts.length === 0 ? (
            <VStack py={12} spacing={4}>
              <Text fontSize="lg" color="gray.500">
                No products found matching your search.
              </Text>
              <Button
                colorScheme="purple"
                onClick={handleClearSearch}
              >
                Clear Search
              </Button>
            </VStack>
          ) : (
            <Grid
              templateColumns={{
                base: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
              gap={5}
            >
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </Grid>
          )}
        </VStack>
      </Grid>
    </Container>
  );
};

export default AllProducts;
